import { MaterialInput } from "app/components"
import {
    AccountsService,
    IResetPasswordRequest,
} from "app/services/AccountsService"
import { checkPasswordStrength } from "app/utils"
import { FormParser } from "app/utils/form/parser"
import { getRegistationInputs, getWebServicesConfig } from "config"
import * as _ from "lodash"
import * as React from "react"
import { Alert, Button, Col, FormGroup, Modal, Row } from "react-bootstrap"

// the modal has several steps. Define them here
enum Stage {
    promptForID,
    promptForNewPassword,
    showUsername,
}

interface IRecoverPasswordModalProps {
    show?: boolean
    onHide: () => void
    onPasswordRecoveryComplete: (u: string, t: string) => void
}

interface IRecoverPasswordModalState {
    stage: Stage
    show: boolean
    isBusy: boolean
    isErrored: boolean
    errorMessage: string
    passwordRules: string
}

const pwPrompt =
    "Passwords must contain at least 8 characters, at least one uppercase character, at least one lowercase character, at least one number, and at least one of !\"#$%&'()*+,-./;:<=>?@[]^_`{|}~"

class RecoverPasswordModal extends React.Component<
    IRecoverPasswordModalProps,
    IRecoverPasswordModalState
> {
    registrationInputs = _.filter(getRegistationInputs(), (q) => {
        return _.includes(
            [
                "firstname",
                "person.lastName",
                "person.last4Social",
                "person.uniqueID.numeric",
                "person.uniqueID.text",
            ],
            q.name
        )
    })

    private svcArgs: IResetPasswordRequest
    private username: string

    constructor(props, context) {
        super(props, context)

        this.showModal = this.showModal.bind(this)
        this.hideModal = this.hideModal.bind(this)

        this.state = {
            stage: Stage.promptForID,
            show: false,
            isBusy: false,
            isErrored: false,
            errorMessage: null,
            passwordRules: pwPrompt,
        }
    }

    /**
     * Process the action next/login button
     *
     * @param e
     */
    advanceStep(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()
        switch (this.state.stage) {
            case Stage.promptForID:
                const args = FormParser.parseInputsWithPaths<any>(
                    e.currentTarget
                )
                this.svcArgs = {
                    Initial: args.firstname[0],
                    Lastname: args.person.lastName,
                    UniqueID: args.person.uniqueID,
                    Password: undefined,
                }
                this.checkAccountExists(this.svcArgs)
                break

            case Stage.promptForNewPassword:
                const args2 = FormParser.parseInputsWithPaths<{
                    password: string
                }>(e.currentTarget)
                this.svcArgs.Password = args2.password
                this.recoverPassword(this.svcArgs)
                break

            case Stage.showUsername:
                this.props.onPasswordRecoveryComplete(
                    this.username,
                    this.svcArgs.Password
                )
                break

            default:
        }
    }

    checkAccountExists(args: any) {
        this.setState({ isBusy: true, isErrored: false })
        const svc = new AccountsService(getWebServicesConfig().accountsEndpoint)
        svc.isPotentialUser(args)
            .then((x) => {
                if (x.isPotentialUser && x.username) {
                    this.username = x.username
                    this.setState({
                        stage: Stage.promptForNewPassword,
                        isErrored: false,
                    })
                } else {
                    const errorMessage =
                        "That information doesn't match our records"
                    this.setState({ isErrored: true, errorMessage })
                }
            })
            .finally(() => {
                this.setState({ isBusy: false })
            })
    }

    hideModal() {
        this.setState({ show: false })
    }

    onHide() {
        const stage = Stage.promptForID
        const isErrored = false
        this.setState({ stage, isErrored })
        this.props.onHide()
    }

    recoverPassword(args: any) {
        const ruleErrors = checkPasswordStrength(args.Password)
        if (ruleErrors) {
            const isErrored = true
            const passwordRules = ruleErrors
            const errorMessage = "That password isn't strong enough." //ruleErrors + "."
            this.setState({
                isErrored,
                errorMessage,
                passwordRules,
            })
            return
        }

        this.setState({ isBusy: true, isErrored: false })
        const svc = new AccountsService(getWebServicesConfig().accountsEndpoint)
        svc.resetPassword(args)
            .then(() => {
                this.setState({ stage: Stage.showUsername, isErrored: false })
            })
            .finally(() => {
                this.setState({ isBusy: false })
            })
    }

    showModal() {
        this.setState({ show: true })
    }

    render() {
        const actionButtonText =
            this.state.stage === Stage.showUsername ? "Login" : "Next"

        return (
            <Modal show={this.props.show} onHide={() => this.onHide()}>
                <Modal.Header closeButton>
                    <Modal.Title>Recover Your Password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form
                        name="loginForm"
                        onSubmit={(e) => this.advanceStep(e)}
                    >
                        {Stage.promptForID === this.state.stage && (
                            <Row>
                                <h5>Enter some personal details</h5>
                                {this.registrationInputs.map(
                                    (registrationInput) => (
                                        <Col
                                            md={6}
                                            key={registrationInput.name}
                                        >
                                            <FormGroup>
                                                <MaterialInput
                                                    name={
                                                        registrationInput.name
                                                    }
                                                    placeholder={
                                                        registrationInput.placeholder
                                                    }
                                                    type={
                                                        registrationInput.type
                                                    }
                                                    required
                                                />
                                            </FormGroup>
                                        </Col>
                                    )
                                )}
                            </Row>
                        )}

                        {Stage.promptForNewPassword === this.state.stage && (
                            <Row>
                                <h5>Choose a new password</h5>
                                <Col md={6}>
                                    <FormGroup>
                                        <MaterialInput
                                            name={"password"}
                                            placeholder={"password"}
                                            type={"password"}
                                            required
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={12}>
                                    <p
                                        dangerouslySetInnerHTML={{
                                            __html: this.state.passwordRules,
                                        }}
                                    ></p>
                                </Col>
                            </Row>
                        )}

                        {Stage.showUsername === this.state.stage && (
                            <Row>
                                <h5>Password recovery complete!</h5>
                                <p>
                                    Your username is{" "}
                                    <a href="#">{this.username}</a>. Remember it
                                    for the next time that you login.
                                </p>
                            </Row>
                        )}

                        <br />

                        {this.state.isErrored ? (
                            <FormGroup>
                                <Alert
                                    bsStyle="danger"
                                    className="animated fadeIn"
                                >
                                    <strong>
                                        <i className="fa fa-fw fa-exclamation-circle" />
                                        <span>Uh oh!&nbsp;</span>
                                    </strong>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: this.state.errorMessage,
                                        }}
                                    ></span>
                                </Alert>
                            </FormGroup>
                        ) : null}

                        <FormGroup>
                            <Button
                                type="submit"
                                bsStyle="primary"
                                bsSize="lg"
                                disabled={this.state.isBusy}
                            >
                                {this.state.isBusy
                                    ? "working..."
                                    : actionButtonText}
                            </Button>
                        </FormGroup>
                    </form>
                </Modal.Body>
            </Modal>
        )
    }
}

export { RecoverPasswordModal }



// WEBPACK FOOTER //
// ./src/app/components/RecoverPasswordModal/index.tsx